// Carries two ids from the storefront into the add-to-cart form, so the order webhook can read
// them off the line item. Separate from uhs-options because ready-to-ship products render no
// option family and therefore no options module.
//
// `_uhs_fs` is attribution: it exists only when this page load arrived through a finder result card
// or a request dialog suggestion card, and it is the only causal link between a finder or request-
// dialog session and a sale. `_uhs_vid` is the theme's own
// visitor id (`visitorId()`), stamped when analytics is allowed — a join key for behavioural
// questions, never a reason to credit the finder for an order.

import { createTracker, finderSessionId, visitorId } from 'uhs-finder.metrics';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// Reuses an existing input: two stamps in one page load must not leave the checkout two values.
function stamp(form, key, value) {
  const name = `properties[${key}]`;
  let input = form.querySelector(`input[name="${CSS.escape(name)}"]`);

  if (!input) {
    input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    form.appendChild(input);
  }

  input.value = value;
}

export function applyAttribution(search) {
  // Same selector uhs-options falls back to, so both find the same form.
  const form = document.querySelector('form[action*="/cart/add"]');
  if (!form) return;

  // Needs no finder visit, unlike the session id below: an order from a shopper who never opened
  // the finder is the denominator, not noise. A stale stamp goes once analytics is turned off.
  const visitor = visitorId();
  if (visitor && UUID.test(visitor)) stamp(form, '_uhs_vid', visitor);
  else form.querySelector('input[name="properties[_uhs_vid]"]')?.remove();

  const sessionId = finderSessionId(search);
  if (!sessionId) return;

  stamp(form, '_uhs_fs', sessionId);
}

// The metrics endpoint, hardcoded the way uhs-finder.dom.js defaults it — this page has no
// finder section to read it from.
const METRICS_ENDPOINT = '/apps/uhs/finder-event';

// Splits "clicked, no order" into never-added vs cart-abandoned. The endpoint requires a session id,
// and only a finder or request-suggestion click-through carries one. A product-page dialog session
// reaches here too, stored without a source; every reader of added_to_cart keys on folded finder
// sessions, so it counts nowhere. Repeated submits are not deduped — the read side counts sessions.
export function trackAddedToCart(search) {
  const sessionId = finderSessionId(search);
  if (!sessionId) return;

  createTracker(METRICS_ENDPOINT, sessionId)('added_to_cart');
}

// One listener at a time, so a re-init cannot stack handlers and double-send beacons.
let submitListener = null;

// `search` is captured here, once per page load: theme.js rewrites the product URL to
// ?variant=<id> on variant selection, discarding everything else in the query string — a
// submit-time read silently loses both ids the moment a variant is picked.
export function initAttribution(search) {
  applyAttribution(search);

  if (submitListener) document.removeEventListener('submit', submitListener, true);

  // The flex-PDP template replaces the whole form subtree on a variant change and fires no
  // event, so re-apply in the capture phase, before either submit handler builds its FormData.
  // The beacon is gated on the cart form: this listener hears every form on the page — search,
  // newsletter, localization — and each would otherwise mint a false added_to_cart.
  submitListener = event => {
    applyAttribution(search);
    if (event.target?.closest?.('form[action*="/cart/add"]')) trackAddedToCart(search);
  };

  document.addEventListener('submit', submitListener, true);
}

initAttribution(window.location.search);
